import React from 'react';
import { GraduationCap, Clock, BookOpen, Languages, Mail, Phone, Calculator } from 'lucide-react';
import { teachersData } from '../data/teachersData';

const TeacherIcon = ({ type }: { type: 'literature' | 'math' | 'languages' }) => {
  const icons = {
    literature: <BookOpen className="h-16 w-16 text-blue-600" />,
    math: <Calculator className="h-16 w-16 text-purple-600" />,
    languages: <Languages className="h-16 w-16 text-green-600" />
  };

  return (
    <div className="relative w-40 h-40 mx-auto mb-6">
      <div className="absolute inset-0 bg-blue-100 rounded-[2.5rem] rotate-3" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div>
          {icons[type]}
        </div>
      </div>
    </div>
  );
};

const Teachers = () => {
  return (
    <div className="page-surface py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="mb-14 max-w-3xl">
          <p className="eyebrow">Echipa Centrului Progres</p>
          <h1 className="page-heading mt-5 font-bold">Profesori care explică până se leagă.</h1>
          <p className="mt-5 text-xl text-gray-600">Pregătire solidă, răbdare și metode adaptate fiecărui elev.</p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
          {teachersData.map((teacher, index) => (
            <article key={index} className="overflow-hidden rounded-[2rem] border border-blue-100 bg-white shadow-float transition-transform duration-300 hover:-translate-y-1 hover:shadow-lift">
              {/* Animated Icon */}
              <div className="bg-blue-50 p-7">
                <TeacherIcon type={teacher.iconType} />
                <h3 className="text-2xl font-bold text-center text-gray-800">{teacher.name}</h3>
                <p className="text-blue-600 text-center font-medium">{teacher.role}</p>
              </div>

              {/* Teacher Info */}
              <div className="p-6 space-y-6">
                <p className="text-gray-600">{teacher.description}</p>

                {/* Experience */}
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                  <span className="font-medium">{teacher.experience}</span>
                </div>

                {/* Education */}
                <div>
                  <div className="flex items-center mb-2">
                    <GraduationCap className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                    <h4 className="font-semibold">Educație</h4>
                  </div>
                  <ul className="list-disc list-inside text-gray-600 ml-7 space-y-1">
                    {teacher.education.map((edu, idx) => (
                      <li key={idx}>{edu}</li>
                    ))}
                  </ul>
                </div>

                {/* Languages - Simple List */}
                <div>
                  <div className="flex items-center mb-2">
                    <Languages className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                    <h4 className="font-semibold">Limbi străine</h4>
                  </div>
                  <p className="text-gray-600 ml-7">
                    {teacher.languages.map(lang => lang.language).join(', ')}
                  </p>
                </div>

                {/* Contact Info - Only if available */}
                {teacher.contact && (
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    {teacher.contact.email && (
                      <div className="flex items-center">
                        <Mail className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                        <a href={`mailto:${teacher.contact.email}`} className="text-blue-600 hover:underline">
                          {teacher.contact.email}
                        </a>
                      </div>
                    )}
                    {teacher.contact.phone && (
                      <div className="flex items-center">
                        <Phone className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                        <a href={`tel:${teacher.contact.phone}`} className="text-blue-600 hover:underline">
                          {teacher.contact.phone}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Teachers;
